import { call, put, select, takeEvery } from 'redux-saga/effects';

import * as authSelectors from '@redux/reducers/auth';
import API_BASE_URL from '@redux/sagas/settings/apibaseurl';
import * as alerts from '@redux/utils/alerts';
import camelcaseKeys from 'camelcase-keys';
import { normalize } from 'normalizr';
import { appIntl } from '../../../../utility/context/IntlGlobalProvider';
import * as entitySelectors from '../../Entities/reducers';
import * as actions from '../actions';
import * as selectors from '../reducers';
import * as schemas from '../schemas';
import * as types from '../types';

/* -------------------------------------------------------------------------- */
/*                                 FETCH LIST                                 */
/* -------------------------------------------------------------------------- */
function* fetchSubdivisionsList(action) {
  const { operatorId: filterOperatorId, organizationId: filterOrganizationId } = action.payload;
  const { operatorId, organizationId } = yield select(authSelectors.getAuthUserMembershipFilters, {
    filterOperatorId,
    filterOrganizationId
  });

  try {
    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);
      const response = yield call(fetch, `${API_BASE_URL}/subdivision/get`, {
        method: 'POST',
        body: JSON.stringify({
          operatorId,
          organizationId
        }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const resultData = jsonResult.data.map((result) => camelcaseKeys(result));
        const {
          entities: { subdivisions },
          result //order
        } = normalize(resultData, schemas.subdivisions); //normalize data to byId and order
        yield put(
          actions.completeFetchingSubdivisions(subdivisions, result, operatorId, organizationId)
        );
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        // alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failFetchingSubdivisions(error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
    // alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failFetchingSubdivisions(error));
  }
}

export function* watchFetchSubdivisionsList() {
  yield takeEvery(types.SUBDIVISIONS_FETCH_STARTED, fetchSubdivisionsList);
}
/* -------------------------------------------------------------------------- */
/*                             PARTIAL FETCH LIST                             */
/* -------------------------------------------------------------------------- */
function* partialFetchSubdivisionsList(action) {
  try {
    const {
      entityTypeId,
      operatorId: filterOperatorId,
      organizationId: filterOrganizationId
    } = action.payload;
    const date = yield select(entitySelectors.getUserUpdateByEntityDate, entityTypeId);
    const { operatorId, organizationId } = yield select(
      authSelectors.getAuthUserMembershipFilters,
      {
        filterOperatorId,
        filterOrganizationId
      }
    );

    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);
      const response = yield call(fetch, `${API_BASE_URL}/subdivision/get`, {
        method: 'POST',
        body: JSON.stringify({ userFetchDate: date, operatorId, organizationId }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const resultData = jsonResult.data.map((result) => camelcaseKeys(result));
        const {
          entities: { subdivisions },
          result //order
        } = normalize(resultData, schemas.subdivisions); //normalize data to byId and order
        yield put(
          actions.completePartialFetchingSubdivisions(
            subdivisions,
            result,
            operatorId,
            organizationId
          )
        );
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        // alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failPartialFetchingSubdivisions(error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
    // alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failPartialFetchingSubdivisions(error));
  }
}

export function* watchPartialFetchSubdivisionsList() {
  yield takeEvery(types.SUBDIVISIONS_PARTIAL_FETCH_STARTED, partialFetchSubdivisionsList);
}
/* -------------------------------------------------------------------------- */
/*                                     ADD                                    */
/* -------------------------------------------------------------------------- */
function* addSubdivision(action) {
  const subdivision = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);

    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/subdivision/save`, {
        method: 'POST',
        body: JSON.stringify({ ...subdivision, subdivisionId: null }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const addedSubdivision = camelcaseKeys(jsonResult.data);
        yield put(actions.completeAddingSubdivision(subdivision.subdivisionId, addedSubdivision));
        alerts.showSuccessCreatedMessage({
          object: appIntl().formatMessage({
            id: 'subdivisions.subdivision'
          }),
          name: subdivision.organizationName
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        // alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failAddingSubdivision(subdivision.subdivisionId, error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
    // alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failAddingSubdivision(subdivision.subdivisionId, error));
  }
}

export function* watchAddSubdivisionsStarted() {
  yield takeEvery(types.SUBDIVISION_ADD_STARTED, addSubdivision);
}

/* -------------------------------------------------------------------------- */
/*                                    EDIT                                    */
/* -------------------------------------------------------------------------- */
function* editSubdivision(action) {
  const subdivision = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);

    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/subdivision/save`, {
        method: 'POST',
        body: JSON.stringify(subdivision),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const editedSubdivision = camelcaseKeys(jsonResult.data);
        yield put(actions.completeEditingSubdivision(editedSubdivision));
        alerts.showSuccessEditedMessage({
          object: appIntl().formatMessage({
            id: 'subdivisions.subdivision'
          }),
          name: editedSubdivision.subdivisionName
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        const oldSubdivision = yield select((state) =>
          selectors.getSubdivision(state, subdivision.subdivisionId)
        );
        yield put(actions.failEditingSubdivision(oldSubdivision, error));
        // alerts.showErrorAlertFailChanges({ error });
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };

    const oldSubdivision = yield select((state) =>
      selectors.getSubdivision(state, subdivision.subdivisionId)
    );
    yield put(actions.failEditingSubdivision(oldSubdivision, error));
    // alerts.showErrorAlertFailChanges({ error });
  }
}

export function* watchEditSubdivisionsStarted() {
  yield takeEvery(types.SUBDIVISION_EDIT_STARTED, editSubdivision);
}

/* -------------------------------------------------------------------------- */
/*                                   REMOVE                                   */
/* -------------------------------------------------------------------------- */
function* removeSubdivision(action) {
  const { subdivisionId } = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);
    const deletedSubdivision = yield select((state) =>
      selectors.getSubdivision(state, subdivisionId)
    );
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);
      const response = yield call(fetch, `${API_BASE_URL}/subdivision/delete`, {
        method: 'POST',
        body: JSON.stringify({ subdivisionId }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        yield put(actions.completeRemovingSubdivision(subdivisionId));
        alerts.showSuccessDeletedMessage({
          object: appIntl().formatMessage({
            id: 'subdivisions.subdivision'
          }),
          name: deletedSubdivision.subdivisionName
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        // alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failRemovingSubdivision(subdivisionId, error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
    // alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failRemovingSubdivision(subdivisionId, error));
  }
}

export function* watchRemoveSubdivisionStarted() {
  yield takeEvery(types.SUBDIVISION_REMOVE_STARTED, removeSubdivision);
}
